import React, { useEffect, useState } from 'react';
import { getIntakeQuestions, createProject, submitProjectIntake } from '../api';
import './ProjectIntakeModal.css';

/**
 * ProjectIntakeModal
 *
 * Full flow for creating a project:
 *   Step 0 — name + description
 *   Step 1 — persona-detection questionnaire (fetched from the server;
 *            goal/behavior-based, never "which persona are you", and the
 *            user never sees persona labels)
 *   Step 2 — optional capital figures (drives the risk advisory)
 *
 * On finish, the SERVER scores the persona vector, selects and computes
 * the KPI set, runs the capital risk read, and generates the project's
 * initial results chat — all with zero LLM token spend. Every question
 * is skippable by design (product guardrail: don't force precision;
 * encourage openness with rough figures).
 */
const ProjectIntakeModal = ({ onClose, onCreated }) => {
  const [step, setStep] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(true);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [answers, setAnswers] = useState({});
  const [capital, setCapital] = useState({ available: '', committed: '', monthlyBurn: '' });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await getIntakeQuestions();
        setQuestions(res.data.questions || []);
      } catch {
        setQuestions([]); // intake still works — persona falls back to defaults
      } finally {
        setQuestionsLoading(false);
      }
    })();
  }, []);

  const setAnswer = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));

  const toggleMulti = (key, value, max) => {
    setAnswers((a) => {
      const current = Array.isArray(a[key]) ? a[key] : [];
      if (current.includes(value)) return { ...a, [key]: current.filter((v) => v !== value) };
      if (max && current.length >= max) return a;
      return { ...a, [key]: [...current, value] };
    });
  };

  const parseCapital = () => {
    const out = {};
    Object.entries(capital).forEach(([k, raw]) => {
      if (raw === '' || raw === null) return;
      const num = Number(raw);
      if (!Number.isNaN(num) && num >= 0) out[k] = num;
    });
    return out;
  };

  const handleFinish = async () => {
    setSubmitting(true);
    setError('');
    try {
      const createRes = await createProject({ name: name.trim(), description: description.trim() || null });
      const project = createRes.data.project;
      const intakeRes = await submitProjectIntake(project._id, {
        answers,
        capital: parseCapital(),
      });
      onCreated(intakeRes.data.project, intakeRes.data.session);
    } catch (err) {
      setError(err.message || 'Could not create the project — try again.');
      setSubmitting(false);
    }
  };

  const steps = ['About the project', 'A few quick questions', 'Capital (optional)'];

  return (
    <div className="pim" role="dialog" aria-modal="true" aria-label="New project">
      <div className="pim__backdrop" onClick={onClose} />
      <div className="pim__card glass-card">
        <div className="pim__header">
          <span className="pim__eyebrow">New Project — Step {step + 1} of {steps.length}</span>
          <h2 className="pim__title">{steps[step]}</h2>
          {step === 1 && (
            <p className="pim__sub">
              This shapes which metrics the project tracks. Rough answers are fine — skip anything you're unsure about.
            </p>
          )}
          {step === 2 && (
            <p className="pim__sub">
              Estimates are welcome. These figures power the project's capital risk read — you can add or change them any time.
            </p>
          )}
        </div>

        <div className="pim__body">
          {step === 0 && (
            <>
              <label className="pim__field">
                <span className="pim__field-label">Project name</span>
                <input
                  className="pim__input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Food truck launch, Duplex renovation, Q4 campaign"
                  maxLength={150}
                  autoFocus
                />
              </label>
              <label className="pim__field">
                <span className="pim__field-label">Description <em>(optional)</em></span>
                <textarea
                  className="pim__input pim__input--textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What is this project about?"
                  maxLength={600}
                  rows={3}
                />
              </label>
            </>
          )}

          {step === 1 && (
            questionsLoading ? (
              <p className="pim__note">Loading questions…</p>
            ) : questions.length === 0 ? (
              <p className="pim__note">Questions unavailable — you can continue and the project will use default metrics.</p>
            ) : (
              questions.map((q) => (
                <div key={q.key} className="pim__question">
                  <span className="pim__question-label">{q.label}</span>

                  {q.type === 'boolean' && (
                    <div className="pim__options">
                      {[{ v: true, l: 'Yes' }, { v: false, l: 'No' }].map(({ v, l }) => (
                        <button
                          key={l}
                          type="button"
                          className={`pim__option${answers[q.key] === v ? ' pim__option--selected' : ''}`}
                          onClick={() => setAnswer(q.key, answers[q.key] === v ? undefined : v)}
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                  )}

                  {q.type === 'select' && (
                    <div className="pim__options pim__options--stack">
                      {q.options.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          className={`pim__option${answers[q.key] === opt.value ? ' pim__option--selected' : ''}`}
                          onClick={() => setAnswer(q.key, answers[q.key] === opt.value ? undefined : opt.value)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {q.type === 'multi_select' && (
                    <div className="pim__options pim__options--wrap">
                      {q.options.map((opt) => {
                        const selected = Array.isArray(answers[q.key]) && answers[q.key].includes(opt.value);
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className={`pim__option${selected ? ' pim__option--selected' : ''}`}
                            onClick={() => toggleMulti(q.key, opt.value, q.maxSelections)}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                      {q.maxSelections && <span className="pim__hint">Pick up to {q.maxSelections}</span>}
                    </div>
                  )}
                </div>
              ))
            )
          )}

          {step === 2 && (
            <div className="pim__capital-grid">
              {[
                { key: 'available', label: 'Capital available' },
                { key: 'committed', label: 'Capital already committed' },
                { key: 'monthlyBurn', label: 'Monthly spend (burn)' },
              ].map(({ key, label }) => (
                <label key={key} className="pim__field">
                  <span className="pim__field-label">{label}</span>
                  <div className="pim__input-wrap">
                    <span className="pim__input-prefix">$</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      className="pim__input"
                      value={capital[key]}
                      onChange={(e) => setCapital((c) => ({ ...c, [key]: e.target.value }))}
                      placeholder="0"
                    />
                  </div>
                </label>
              ))}
            </div>
          )}

          {error && <p className="pim__error">{error}</p>}
        </div>

        <div className="pim__nav">
          <button className="btn btn--sm btn--ghost" onClick={step === 0 ? onClose : () => setStep((s) => s - 1)} disabled={submitting}>
            {step === 0 ? 'Cancel' : 'Back'}
          </button>
          {step < steps.length - 1 ? (
            <button
              className="btn btn--sm btn--gold"
              onClick={() => setStep((s) => s + 1)}
              disabled={step === 0 && !name.trim()}
            >
              Next
            </button>
          ) : (
            <button className="btn btn--sm btn--gold" onClick={handleFinish} disabled={submitting || !name.trim()}>
              {submitting ? 'Setting up…' : 'Create Project'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectIntakeModal;
